import { UserbotCatalog } from "../../interfaces"
import { parseJsonValue } from "../common/jsonUtils"
import { matchingVariant } from "./fieldSpecVariant"

/** Whole-string плейсхолдер: {{...}} (TEMPLATE_RE из TZ §2.3). */
const isPlaceholder = (v: string): boolean => /^\{\{[\s\S]+\}\}$/.test(v)

/** Скалярные типы, хранимые/вводимые как строка. */
const STRING_TYPES: UserbotCatalog.FieldType[] = [
  "string",
  "text",
  "chatId",
  "clientId",
  "role",
  "entity",
  "llmTask",
  "matcherType",
  "cron",
  "enum",
]

/** Проверяет, валиден ли результат коэрсии для данного типа поля. */
const isValidCoercion = (
  type: UserbotCatalog.FieldType,
  result: unknown,
): boolean => {
  switch (type) {
    case "number":
      return typeof result === "string" || !Number.isNaN(result)
    case "boolean":
      return typeof result === "boolean"
    case "json":
      return result !== undefined
    case "array":
      return Array.isArray(result)
    case "object":
      return typeof result === "object" && result !== null
    default:
      return true
  }
}

/** Сырое значение формы → типизированное значение для DTO. */
export const coerceFieldValue = (
  spec: UserbotCatalog.FieldSpec,
  raw: unknown,
): unknown => {
  // templatable: whole-string {{...}} — не коэрсить, отдать строкой
  if (spec.templatable && typeof raw === "string" && isPlaceholder(raw)) {
    return raw
  }

  switch (spec.type) {
    case "number": {
      const s = String(raw ?? "")
      return isPlaceholder(s) ? s : Number(s)
    }
    case "boolean":
      return raw === true || raw === "true" || raw === "1"
    case "json":
      return parseJsonValue(String(raw ?? ""))
    case "array": {
      const arr = Array.isArray(raw) ? raw : []
      const item = spec.item
      if (!item) return arr
      return arr
        .filter((el) => String(el ?? "").trim() !== "")
        .map((el) => coerceFieldValue(item, el))
    }
    case "object": {
      const obj = (raw ?? {}) as Record<string, unknown>
      const fields = spec.fields ?? []
      const result: Record<string, unknown> = { ...obj }
      for (const f of fields) {
        if (f.key in obj) {
          const v = coerceFieldValue(f, obj[f.key])
          if (v !== undefined) result[f.key] = v
        }
      }
      return result
    }
    case "oneOf": {
      const variants = spec.variants ?? []
      const matched = matchingVariant(variants, raw)
      if (matched && typeof raw === "object")
        return coerceFieldValue(matched, raw)
      // Перебираем варианты в обратном порядке (более специфичные типы раньше)
      for (let i = variants.length - 1; i >= 0; i--) {
        const variant = variants[i]
        const coerced = coerceFieldValue(variant, raw)
        if (isValidCoercion(variant.type, coerced)) {
          return coerced
        }
      }
      // Ни один не подошёл — отдаём как есть (бэк отклонит при save)
      return raw == null ? "" : String(raw)
    }
    default:
      // все STRING_TYPES
      return raw == null ? "" : String(raw)
  }
}

/** Типизированное значение из DTO → сырое значение формы (round-trip). */
export const rawFromFieldValue = (
  spec: UserbotCatalog.FieldSpec,
  value: unknown,
): unknown => {
  if (value === undefined || value === null) {
    if (spec.type === "array") return []
    if (spec.type === "object") return {}
    if (spec.type === "boolean") return "false"
    return ""
  }
  switch (spec.type) {
    case "json":
      return JSON.stringify(value, null, 2)
    case "boolean":
      // This boundary receives DTO/raw JSON values, not UI string representations.
      // A malformed "false", "true" or 1 must never grant consent when re-edited.
      return value === true ? "true" : "false"
    case "array": {
      const arr = Array.isArray(value) ? value : []
      const item = spec.item
      return item ? arr.map((el) => rawFromFieldValue(item, el)) : arr
    }
    case "object": {
      const obj = value as Record<string, unknown>
      const fields = spec.fields ?? []
      const result: Record<string, unknown> = { ...obj }
      for (const f of fields) {
        if (f.key in obj) {
          result[f.key] = rawFromFieldValue(f, obj[f.key])
        }
      }
      return result
    }
    case "oneOf": {
      const variants = spec.variants ?? []
      const matched = matchingVariant(variants, value)
      if (matched) return rawFromFieldValue(matched, value)
      // Сопоставляем по typeof значения
      const tv = typeof value
      for (const variant of variants) {
        const variantIsString = STRING_TYPES.includes(variant.type)
        const variantIsNumber = variant.type === "number"
        const variantIsBoolean = variant.type === "boolean"
        const variantIsObject =
          variant.type === "object" || variant.type === "json"
        const variantIsArray = variant.type === "array"
        if (
          (tv === "string" && variantIsString) ||
          (tv === "number" && variantIsNumber) ||
          (tv === "boolean" && variantIsBoolean) ||
          (tv === "object" && (variantIsObject || variantIsArray))
        ) {
          return rawFromFieldValue(variant, value)
        }
      }
      // Fallback — первый вариант
      return variants.length
        ? rawFromFieldValue(variants[0], value)
        : String(value)
    }
    default:
      return typeof value === "object" ? JSON.stringify(value) : String(value)
  }
}

export { STRING_TYPES }
